import { HtmlBox } from "@/components/ui/html-box";
import { MuiButton } from "@/components/ui/mui-button";
import { MuiSkeleton } from "@/components/ui/mui-skeleton";
import { MuiTypography } from "@/components/ui/mui-typography";
import { MUI_TYPOGRAPHY_VARIANTS } from "@/components/ui/mui-typography-shape";
import { PageContainer } from "@/components/ui/page-container";
import { cn } from "@/lib/utils";

// The geometry both details pages stand in — the authed one and the anonymous
// product page — lifted out of `item-detail-page.tsx` when the second one
// arrived (#309).
//
// **It is one page in the donor.** `ItemDetailsIsland` renders the same frame,
// the same header row and the same two-column grid for both audiences, and
// switches its *contents* on an `isPublic` prop (`ItemDetailsIsland.tsx:63`).
// Atlas splits the two compositions — the payloads are different types and the
// authed page's fourteen props are all authed-only — but a split composition
// must not become a split *layout*: two hand-kept copies of the frame, the
// 32px grid gap and the 900px breakpoint would drift by a few pixels and the
// only place that shows is a screenshot.
//
// So the frame lives here once and takes slots. Everything with a rule behind
// it — the padding, the breakpoint, the band, the skeleton's three heights, the
// `&nbsp;` — is stated in this file and nowhere else.

interface ItemDetailLayoutProps {
  /** The status badge and, on the authed page only, the EI tier chips. */
  badges?: React.ReactNode;
  /**
   * The header row's right-hand side — the action menu.
   *
   * `ItemDetailsIsland.tsx:134` gates it on `!isPublic`, so the anonymous page
   * passes nothing and the row keeps its height for the badges alone.
   */
  actions?: React.ReactNode;
  /** The left grid cell: the gallery and the EI mark anchored to it. */
  image: React.ReactNode;
  /** The right grid cell: manufacturer, name, and everything under them. */
  meta: React.ReactNode;
  /**
   * Whether the page's own frame stands around it. The donor's `PageContainer`
   * is the *page* host's (`item-details-page.tsx:12`), not the island's, so in
   * its dialog mode — the spec details page's read-only item dialog (#695) —
   * the island's content sits straight in the dialog's own padding.
   */
  framed?: boolean;
}

export function ItemDetailLayout({
  badges,
  actions,
  image,
  meta,
  framed = true,
}: ItemDetailLayoutProps) {
  const content = (
    <>
      {/* `text.primary`, which the donor's text inherits from `body` — its
          `Typography` sets no colour — and this app's `body` does not carry. */}
      <div data-testid="item-details-content" className="text-lennar-ink bg-white">
        {/* Badges left, the action menu hard right (`Header/Container.tsx`), with
          its own margins rather than the page's padding because the menu button
          carries 16px of clearance of its own. */}
        {/* `alignItems='center'` (`Header/Container.tsx:13`) — with EI chips in
          the row their own top padding would otherwise push the badges below
          the menu button. */}
        <div className="mx-4 mt-1 mb-4 flex items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">{badges}</div>
          {actions}
        </div>

        {/* Two columns from 900px. MUI's `md` breakpoint is 900px, not
          Tailwind's 768px, so the arbitrary `min-[900px]:` is the faithful one —
          `md:` would flip the page to two columns 132px early. */}
        <div className="grid grid-cols-1 gap-8 bg-white px-4 min-[900px]:grid-cols-2">
          {/* The image column. The `mb: 3` that separates the picture from the
            metadata once the page stacks belongs to `ImagePreview.tsx:28`'s box,
            which is `ItemGallery`'s — carrying it here as well would double it. */}
          <div>{image}</div>
          <div>{meta}</div>
        </div>
      </div>
    </>
  );
  // The donor's `PageContainer`, outside the island's `mx: 2`/`px: 2`
  // (`item-details-page.tsx:12`). The authed chrome supplies no gutter,
  // deliberately, so each page owns it — which is also why the anonymous
  // page, which has no chrome at all, needs no gutter of its own.
  return framed ? <PageContainer>{content}</PageContainer> : content;
}

/**
 * The donor's 8px `background.default` band between sections (`Divider.tsx`).
 *
 * **Bands, not rules**, and both of them are *inside the right column*
 * (`ItemDetailsIsland.tsx:178-186`): one above the Details table, one between
 * the manufacturer-site button and the Description. A full-width band would cut
 * across the picture, which the donor's never does.
 *
 * The `mb-2` is the donor's `<hr>` speaking: `Divider` is `Box component='hr'`
 * with `mt: 3` and no `mb`, so the UA stylesheet's `margin-block: 0.5em` — 8px
 * at the body's 16px — stands under it. Under a heading it collapses into the
 * heading's 16px and shows nowhere; under the last band, on an item with no
 * description, it is the 8px the page came up short of modsy by (#553 D6).
 */
export function SectionBand() {
  return (
    <div
      aria-hidden="true"
      data-testid="section-band"
      className="bg-catalog-section-band mt-6 mb-2 h-2 w-full"
    />
  );
}

/** `Metadata/MetaLoadingSkeleton.tsx` — 256, 384 and 128px, in that order. */
export function DetailSkeleton() {
  return (
    <div data-testid="pdp-loading-skeleton" className="flex w-full flex-col gap-3 pb-4">
      {/* `rounded` + `wave` said out loud. Both used to be this component's
          defaults; they are MUI's `text` + `pulse` now, because that is what
          MUI itself defaults to and what the catalog table's skeletons need.
          The donor's metadata loader passes these two explicitly
          (`Metadata/MetaLoadingSkeleton.tsx:9-11`), so this now reads the same
          way its source does. */}
      <MuiSkeleton variant="rounded" animation="wave" className="h-64 w-full" />
      <MuiSkeleton variant="rounded" animation="wave" className="h-96 w-full" />
      <MuiSkeleton variant="rounded" animation="wave" className="h-32 w-full" />
    </div>
  );
}

interface ItemMetaHeadingProps {
  manufacturerName: string | null | undefined;
  itemName: string | null | undefined;
}

/**
 * `Metadata/Manufacturer.tsx` and `Metadata/Name.tsx`, the two lines that
 * **outlive the load**.
 *
 * The donor mounts both above its loading branch (`ItemDetailsIsland
 * .tsx:154-158`) and skeletons only what is below them. That is what the
 * trailing `&nbsp;` is for: the manufacturer line holds its height while empty,
 * so nothing jumps when the item lands.
 */
export function ItemMetaHeading({ manufacturerName, itemName }: ItemMetaHeadingProps) {
  return (
    <>
      {/* `subtitle1` with the donor's own `fontWeight: 600`, and its
        deliberate trailing `&nbsp;`. A paragraph, as it has been here: the
        page's one heading is the item's name. */}
      <MuiTypography
        data-testid="pdp-manufacturer"
        variant="subtitle1"
        component="p"
        className="mb-2 font-semibold"
      >
        {manufacturerName}&nbsp;
      </MuiTypography>
      {/* `h4` with `overflowWrap: 'break-word'` and `mb: 1.5`. The page's only
        heading, so it is the document's `h1`. */}
      <MuiTypography
        data-testid="pdp-item-name"
        variant="h4"
        component="h1"
        className="mb-3 break-words"
      >
        {itemName}
      </MuiTypography>
    </>
  );
}

/**
 * `Metadata/CtaButtons.tsx`' Add Variant — outlined, large, full width, 16px
 * under the variant rows (`sx={{ mt: 2 }}`).
 *
 * Authed only: the donor draws its `CtaButtons` under `!readOnly && !isPublic`
 * (`ItemDetailsIsland.tsx:179`), so the anonymous product page never mounts
 * this. **Disabled rather than absent** when it cannot run, as the donor's is
 * (`disabled={isUnspecifiedManufacturer}`); the reason is the menu entry's own
 * (`addVariantBlock`), so the two ways into the dialog cannot disagree. The
 * donor's Select and Remove Selected CTAs belong to the spec-selection flow,
 * which the catalog page has none of.
 */
export function AddVariantButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <MuiButton
      data-testid="pdp-dialog-add-variant-btn"
      variant="outlined"
      size="large"
      fullWidth
      disabled={disabled}
      onClick={onClick}
      className="mt-4"
    >
      Add Variant
    </MuiButton>
  );
}

/**
 * `Details/ManufacturerSite.tsx` — the outlined, full-width link out.
 *
 * **Absent, not disabled**, when there is no site to visit: the donor renders
 * nothing at all rather than a dead button. Ungated on the audience
 * (`ItemDetailsIsland.tsx:184` sits outside every `!isPublic`), so both pages
 * draw it — which is why it is here rather than written out twice.
 *
 * The href arrives under two different names — `url` on the authed payload,
 * `externalUrl` on the public one — so this takes the resolved value rather
 * than either item.
 */
export function ManufacturerSiteButton({ href }: { href: string | null | undefined }) {
  if (!href) return null;

  return (
    // `<Button size='large' variant='outlined' fullWidth sx={{mt:3}}
    // component='a' href target='_blank'>` — the shared button's anchor form.
    // `asChild` hands the shape to the `<a>`, and `rippleAsChild` puts the
    // press ripple inside it, as `ButtonBase` draws one on the donor's anchor
    // too (#740 D5, owner).
    <MuiButton variant="outlined" size="large" fullWidth asChild rippleAsChild className="mt-6">
      <a
        data-testid="pdp-manufacturer-site"
        href={href}
        target="_blank"
        // `noreferrer` rides along with `noopener` here: the anonymous page is
        // reachable without a session, so the referrer is the only thing that
        // would tell a manufacturer's site which item page a stranger came
        // from.
        rel="noopener noreferrer"
      >
        View Manufacturer Site
      </a>
    </MuiButton>
  );
}

/**
 * `Details/Description.tsx` — heading and body both, or neither.
 *
 * Ungated on the audience too (`ItemDetailsIsland.tsx:186`). The HTML is
 * sanitised by Python on the way out and rendered only through `HtmlBox`,
 * which is spec #292's one sanctioned raw-HTML render site — a boundary that
 * must not be re-implemented per page, which is a second reason this block has
 * one home.
 */
export function ItemDescriptionSection({ html }: { html: string | null | undefined }) {
  if (!html) return null;

  return (
    <>
      {/* `<Typography variant='subtitle1' sx={{ my: 2 }}>`, as the section's
          `h2`. */}
      <MuiTypography variant="subtitle1" component="h2" className="my-4">
        Description
      </MuiTypography>
      {/* `Description.tsx:22-25`: `Typography variant='body2' sx={{ mb: 3 }}`
          over the markup, with no colour of its own — so `body2` outright in
          `text.primary`, and none of the list row's 400px lane, which in a
          page column would force a horizontal scrollbar. */}
      <HtmlBox
        data-testid="item-description"
        html={html}
        className={cn(MUI_TYPOGRAPHY_VARIANTS.body2, "text-lennar-ink mb-6")}
      />
    </>
  );
}
